import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionHead, SpotlightCard } from "@/components/Shared";

const PROJECTS = [
  {
    img: "/bluecry.jpg",
    meta: "Blue Cry Foundation",
    tags: ["Non-Profit", "Website"],
    wide: true,
    href: "https://bluecryfoundation.org/#home",
    contain: true,
  },
  {
    img: "https://images.unsplash.com/photo-1642132652860-471b4228023e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    meta: "Business Dashboard",
    tags: ["Web App", "Design", "Development"],
    wide: false,
  },
  {
    img: "https://images.unsplash.com/photo-1487014679447-9f8336841d58?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    meta: "Brand Website",
    tags: ["Website", "Branding"],
    wide: false,
  },
  {
    img: "https://images.unsplash.com/photo-1507238691740-187a5b1d37b8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    meta: "Booking & Landing Page",
    tags: ["Landing Page", "SEO"],
    wide: true,
  },
];

function ProjectCard({ p, i }) {
  const linked = Boolean(p.href);
  return (
    <SpotlightCard
      as={linked ? "a" : "div"}
      href={p.href}
      target={linked ? "_blank" : undefined}
      rel={linked ? "noopener noreferrer" : undefined}
      aria-label={linked ? `Visit the ${p.meta} website` : undefined}
      testid={`portfolio-card-${i + 1}`}
      className={`group relative block h-full overflow-hidden rounded-2xl border hairline bg-card ${
        p.wide ? "lg:col-span-2" : ""
      }`}
    >
      <div className="relative aspect-[4/3] h-full w-full overflow-hidden sm:aspect-[16/10] lg:aspect-auto lg:min-h-[340px]">
        <img
          src={p.img}
          alt={linked ? `${p.meta} website` : p.meta}
          loading="lazy"
          className={`absolute inset-0 h-full w-full transition-[transform,opacity,filter] duration-700 group-hover:scale-[1.04] ${
            p.contain
              ? "bg-white object-contain p-6 sm:p-10"
              : "object-cover opacity-80 saturate-[0.8] group-hover:opacity-100 group-hover:saturate-100"
          }`}
        />
        <div
          className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent"
          aria-hidden="true"
        />
        {linked && (
          <span className="absolute left-4 top-4 rounded-full border border-white/20 bg-ink/80 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em] text-txt backdrop-blur">
            Live Website
          </span>
        )}
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-6 md:p-7">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cy/90">
              {String(i + 1).padStart(2, "0")}
            </p>
            <h3 className="mt-2 font-grotesk text-lg font-semibold tracking-tight text-txt sm:text-xl">
              {p.meta}
            </h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {p.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-full border border-white/10 bg-white/[0.05] px-3 py-1 text-[11px] text-sub"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
          <span
            className={`hidden h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/15 bg-ink/60 text-txt backdrop-blur transition-all duration-500 sm:flex ${
              linked ? "opacity-100" : "opacity-0 group-hover:opacity-100"
            }`}
            aria-hidden="true"
          >
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </div>
      </div>
    </SpotlightCard>
  );
}

export default function Work() {
  return (
    <section id="work" data-testid="portfolio-section" className="scroll-mt-20 bg-ink2/40 py-24 md:py-32">
      <div className="container-x">
        <SectionHead
          eyebrow="Our Work"
          title="Built to be shown off. Yours will fit right in."
          sub="A showcase of the kind of work we build. Real client projects will take these frames as they are completed."
        />
        <div className="mt-14 grid gap-5 lg:grid-cols-3">
          {PROJECTS.map((p, i) => (
            <Reveal key={p.img} delay={i * 0.07} className={p.wide ? "lg:col-span-2" : ""}>
              <ProjectCard p={p} i={i} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
